// Chart status logic extracted from the inline rendering in constants.js (Patient Info column).
// TODO: Refactor constants.js to consume this shared helper to eliminate duplication.
import React from 'react';
import {
  TeamOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  WarningOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  FolderSimplePlus,
  ListChecks,
  ClipboardText,
  CloudArrowUp,
  FileMagnifyingGlass,
} from '@phosphor-icons/react';
import { ClipboardPlus } from '../../../components/icons';
import { getPacketUploadStatus } from './packetUploadStatus';
import { PAGES } from '../../shared/constants';

const SUCCESS = 'success';
const ERROR = 'error';
const WARNING = 'warning';
const INFO = 'info';
const RESTRICTED_CHART_MESSAGE = 'This chart is restricted and the document was not uploaded.';
const FAILED_FIND_PATIENT_CHART = 'Failed to find the patient chart';

const iconStyles = {
  display: 'block',
};

const phosphorIcon = (IconComponent, color, { weight } = {}) => (
  <IconComponent
    size={22}
    color={color}
    style={iconStyles}
    weight={weight}
  />
);

const antIcon = (IconComponent, color, { spin = false } = {}) => (
  <IconComponent
    spin={spin}
    style={{
      ...iconStyles,
      color,
      fontSize: 22,
    }}
  />
);

const statusResult = ({
  statusKey,
  severity,
  message,
  link = null,
  iconNode,
  retryInsuranceId = null,
}) => {
  const result = {
    statusKey,
    severity,
    message,
    link,
    iconNode,
  };
  if (retryInsuranceId) result.retryInsuranceId = retryInsuranceId;
  return result;
};

const SFTP_CATEGORY_STATUS_CONFIG = {
  in_progress: {
    severity: INFO,
    message: 'This document is being uploaded to SFTP because its AI category is configured as SFTP only.',
    icon: () => antIcon(SyncOutlined, 'orange', { spin: true }),
  },
  retryable: {
    severity: WARNING,
    message: 'SFTP delivery is temporarily delayed and will retry automatically. Automatic EMR upload remains disabled for this document.',
    icon: () => phosphorIcon(CloudArrowUp, 'orange'),
  },
  delivered: {
    severity: SUCCESS,
    message: 'This document was successfully uploaded to SFTP because its AI category is configured as SFTP only.',
    icon: () => phosphorIcon(CloudArrowUp, '#52c41a'),
  },
  failed: {
    severity: ERROR,
    message: 'SFTP delivery could not be completed. Automatic EMR upload remains disabled for this document. Contact support for help.',
    icon: () => antIcon(ExclamationCircleOutlined, '#b31c0e'),
  },
};

const findPatientChartMessage = (orgName) => (
  orgName === 'National Spine & Pain Centers'
    ? 'Failed to find patient chart. Please check the spelling of their name and DOB, or ensure the patient chart exists.'
    : 'Failed to find patient chart. Please check the spelling of their name and DOB, or use the \'Create New Chart\' button if the patient doesn\'t exist.'
);

// Short display labels keyed by statusKey. Shared by the viewer ChartStatus
// chip and the reply drawer chip so the wording cannot drift between surfaces.
export const CHART_STATUS_LABELS = {
  task_created: 'Task Created',
  task_autoupload: 'Synced to EMR',
  insurance_populated_and_task_created: 'Chart & Task Created',
  chart_created: 'Chart Created',
  existing_chart_reused: 'Existing Chart Used',
  auto_upload_created: 'Synced to EMR',
  task_after_chart_creation_success: 'Chart & Task Created',
  imported_into_emr: 'Uploaded to EMR',
  routed_to_sftp: 'Routed to SFTP',
  sftp_fallback_delivered: 'Uploaded to SFTP',
  sftp_category_in_progress: 'Uploading to SFTP',
  sftp_category_retryable: 'SFTP Delivery Delayed',
  sftp_category_delivered: 'Uploaded to SFTP',
  sftp_category_failed: 'SFTP Delivery Failed',
  document_uploaded_and_insurance_error: 'Uploaded, Check Insurance',
  insurance_failed_and_task_created: 'Insurance Failed',
  task_creation_failed_and_insurance_error: 'Task & Insurance Failed',
  task_creation_error: 'Task Creation Failed',
  chart_filing_unconfirmed: 'Filing Unconfirmed',
  chart_creation_error: 'Chart Creation Failed',
  upload_task_error: 'Upload Failed',
  insurance_not_active: 'Insurance Not Active',
  primary_insurance_already_exists: 'Insurance Already Exists',
  insurance_error: 'Insurance Failed',
  insurance_populated_and_task_creation_error: 'Task Creation Failed',
  chart_task_error: 'Task Error',
  athena_insurance_sync_error: 'Insurance Not Added',
  emr_import_error_multiple_patients: 'Multiple Patients Detected',
  emr_import_error_failed_find_chart: 'Chart Not Found',
  emr_import_error_restricted_chart: 'Restricted Chart',
  emr_import_error: 'Upload Failed',
  insurance_in_progress: 'Insurance In Progress',
  chart_in_progress: 'Creating Chart',
  auto_upload_in_progress: 'Syncing to EMR',
  auto_upload_scheduled: 'Queued for Upload',
  task_in_progress: 'Creating Task',
  task_autoupload_in_progress: 'Syncing to EMR',
  task_insurance_in_progress: 'Processing',
  task_after_chart_creation_in_progress: 'Creating Task',
};

const getDocumentChartStatus = (transmission = {}, { orgName = '' } = {}) => {
  const t = transmission || {};
  const {
    imported_into_emr: importedIntoEMR,
    emr_uploaded_by: emrUploadedBy,
    emr_uploaded_at: emrUploadedAt,
    auto_upload_created: autoUploadCreated,
    auto_upload_in_progress: autoUploadInProgress,
    auto_upload_scheduled: autoUploadScheduled,
    task_in_progress: taskInProgress,
    task_autoupload_in_progress: taskAutoUploadInProgress,
    task_after_chart_creation_in_progress: taskAfterChartInProgress,
    task_after_chart_creation_success: taskAfterChartSuccess,
    chart_task_error: chartTaskError,
    chart_in_progress: chartInProgress,
    insurance_in_progress: insuranceInProgress,
    insurance_not_active: insuranceNotActive,
    primary_insurance_already_exists: primaryInsuranceAlreadyExists,
    chart_created: chartCreated,
    existing_chart_reused: existingChartReused,
    insurance_populated: insurancePopulated,
    task_created: taskCreated,
    task_autoupload: taskAutoUpload,
    emr_import_error: emrImportError,
    task_creation_error: taskCreationError,
    upload_task_error: uploadTaskError,
    chart_creation_error: chartCreationError,
    chart_filing_unconfirmed: chartFilingUnconfirmed,
    chart_filing_unconfirmed_link: chartFilingUnconfirmedLink,
    emr_patient_chart_link: emrPatientChartLink,
    emr_patient_chart_link_insurance: emrPatientChartLinkInsurance,
    insurance_error: insuranceError,
    document_uploaded_and_insurance_error: documentUploadedAndInsuranceError,
    task_insurance_in_progress: taskInsuranceInProgress,
    task_creation_failed_and_insurance_error: taskCreationFailedAndInsuranceError,
    insurance_populated_and_task_created: insurancePopulatedAndTaskCreated,
    insurance_populated_and_task_creation_error: insurancePopulatedAndTaskCreationError,
    insurance_failed_and_task_created: insuranceFailedAndTaskCreated,
    routed_to_sftp: routedToSftp,
    sftp_fallback_outcome: sftpFallbackOutcome,
    sftp_fallback_reason: sftpFallbackReason,
    sftp_category_delivery_outcome: sftpCategoryDeliveryOutcome,
    athena_insurance_retry_id: athenaInsuranceRetryId,
    athena_insurance_sync_outcome: athenaInsuranceSyncOutcome,
  } = t;
  const uploadedAtText = emrUploadedAt || 'an unknown date';

  if (routedToSftp) {
    return statusResult({
      statusKey: 'routed_to_sftp',
      severity: WARNING,
      message: 'Patient chart was not found. The document was successfully routed to the fallback SFTP folder.',
      iconNode: phosphorIcon(CloudArrowUp, 'orange'),
    });
  }

  const sftpCategoryStatus = SFTP_CATEGORY_STATUS_CONFIG[sftpCategoryDeliveryOutcome];
  if (sftpCategoryStatus) {
    return statusResult({
      statusKey: `sftp_category_${sftpCategoryDeliveryOutcome}`,
      severity: sftpCategoryStatus.severity,
      message: sftpCategoryStatus.message,
      iconNode: sftpCategoryStatus.icon(),
    });
  }

  // Before every success branch on purpose. The document row and the insurance row are
  // now read separately, so a record can report a filed document AND a failed insurance
  // write at the same time -- and the success keys below are evaluated first, which
  // would render the row green and hide the failure entirely. At the organizations where
  // the legacy follow-up is the intended insurance writer that failure is the whole
  // insurance outcome, so it has to win here.
  //
  // WARNING, not ERROR: red means "the document is not in the EHR -- act", and here the
  // document landed. Red plus "please try again" is exactly the copy that produced
  // duplicate manual re-files of documents already in the chart (ENG-3252).
  if (documentUploadedAndInsuranceError) {
    return statusResult({
      statusKey: 'document_uploaded_and_insurance_error',
      severity: WARNING,
      message: documentUploadedAndInsuranceError,
      link: emrPatientChartLink,
      iconNode: antIcon(WarningOutlined, 'orange'),
    });
  }

  // Ahead of the success branches for the same reason as the combination key above: the
  // presenter now reports the document row's success next to a follow-up that is still
  // running. The newest row used to be that follow-up, so this spinner won; keep it
  // winning, or a record flips green mid-write and back to orange if the write fails.
  if (insuranceInProgress) {
    return statusResult({
      statusKey: 'insurance_in_progress',
      severity: INFO,
      message: 'Chart creation successful, insurance information is currently being populated in the EMR.',
      link: emrPatientChartLinkInsurance || emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  // A filed document must not hide an inactive-insurance failure behind its green
  // success chip. Keep this limited to document-success states: task-only and
  // failed-document outcomes retain their existing precedence.
  if (insuranceNotActive && insuranceError && (autoUploadCreated || taskAutoUpload || chartCreated)) {
    return statusResult({
      statusKey: 'insurance_not_active',
      severity: WARNING,
      message: insuranceNotActive,
      link: emrPatientChartLinkInsurance || emrPatientChartLink,
      iconNode: antIcon(WarningOutlined, 'orange'),
    });
  }

  if (taskCreated) {
    const message = emrUploadedBy
      ? `${emrUploadedBy} has created a task for this record on ${uploadedAtText}.`
      : `Task for this record was successfully created in the EMR on ${uploadedAtText}.`;
    return statusResult({
      statusKey: 'task_created',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(ListChecks, '#52c41a'),
    });
  }

  if (taskAutoUpload) {
    const message = emrUploadedBy
      ? `${emrUploadedBy} has successfully synced this record into the Patient's chart and a task was created for it on ${uploadedAtText}.`
      : `This record has successfully synced into the Patient's chart and a task was created for it on ${uploadedAtText}.`;
    return statusResult({
      statusKey: 'task_autoupload',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(ClipboardPlus, '#52c41a'),
    });
  }

  if (insurancePopulatedAndTaskCreated) {
    const message = emrUploadedBy
      ? `${emrUploadedBy} has created the patient chart and task on ${uploadedAtText}, and insurance was successfully populated.`
      : `Patient chart and task were created on ${uploadedAtText}, and insurance was successfully populated.`;
    return statusResult({
      statusKey: 'insurance_populated_and_task_created',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(ClipboardPlus, '#52c41a'),
    });
  }

  if (chartCreated && existingChartReused) {
    // Find-or-create EMRs (CareCloud) file the document to a chart that already matched the
    // patient's name and DOB instead of creating a duplicate. Say so, or the user will go
    // looking for a new chart that was never made.
    const message = emrUploadedBy
      ? `${emrUploadedBy} filed this record to the patient's existing chart on ${uploadedAtText}. A chart with this name and date of birth was already in the EMR, so no new chart was created.`
      : `This record was filed to the patient's existing chart on ${uploadedAtText}. A chart with this name and date of birth was already in the EMR, so no new chart was created.`;
    return statusResult({
      statusKey: 'existing_chart_reused',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(FolderSimplePlus, '#52c41a'),
    });
  }

  if (chartCreated) {
    const message = emrUploadedBy
      ? `${emrUploadedBy} has created the patient chart on ${uploadedAtText}.`
      : `Patient Chart successfully created on ${uploadedAtText}.`;
    return statusResult({
      statusKey: 'chart_created',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(FolderSimplePlus, '#52c41a'),
    });
  }

  if (autoUploadCreated) {
    // A successful follow-up used to make the newest row type 5, so this shape rendered
    // through the importedIntoEMR branch below and its "and patient's insurance was
    // populated" sentence. The document row now wins, so carry that sentence here or the
    // insurance news silently disappears for every record that has one.
    const showInsurance = emrPatientChartLink && insurancePopulated;
    let message;
    if (showInsurance) {
      message = emrUploadedBy
        ? `${emrUploadedBy} has successfully synced this record into the Patient's chart on ${uploadedAtText}, and patient's insurance was populated successfully.`
        : `This record has successfully synced into the Patient's chart by Medsender AI on ${uploadedAtText}, and patient's insurance was populated successfully.`;
    } else {
      message = emrUploadedBy
        ? `${emrUploadedBy} has successfully synced this record into the Patient's chart on ${uploadedAtText}.`
        : `This record has successfully synced into the Patient's chart by Medsender AI on ${uploadedAtText}.`;
    }
    return statusResult({
      statusKey: 'auto_upload_created',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: emrPatientChartLink
        ? phosphorIcon(FolderSimplePlus, '#52c41a')
        : phosphorIcon(ClipboardText, '#52c41a', { weight: 'fill' }),
    });
  }

  if (taskAfterChartSuccess && !taskInsuranceInProgress && !insuranceFailedAndTaskCreated) {
    const message = emrUploadedBy
      ? `${emrUploadedBy} has created the patient chart and assigned a task on ${uploadedAtText}.`
      : `Patient chart successfully created and task has been assigned on ${uploadedAtText}.`;
    return statusResult({
      statusKey: 'task_after_chart_creation_success',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(ClipboardPlus, '#52c41a'),
    });
  }

  if (importedIntoEMR && !taskInsuranceInProgress && !insuranceFailedAndTaskCreated) {
    const showInsurance = emrPatientChartLink && insurancePopulated;
    let message;
    if (showInsurance) {
      message = emrUploadedBy
        ? `${emrUploadedBy} has auto uploaded this record into your EMR on ${uploadedAtText}, and patient's insurance was populated successfully.`
        : `This record was auto uploaded into your EMR by Medsender AI on ${uploadedAtText}, and patient's insurance was populated successfully.`;
    } else {
      message = emrUploadedBy
        ? `${emrUploadedBy} has auto uploaded this record into your EMR on ${uploadedAtText}.`
        : `This record was auto uploaded into your EMR by Medsender AI on ${uploadedAtText}.`;
    }
    return statusResult({
      statusKey: 'imported_into_emr',
      severity: SUCCESS,
      message,
      link: emrPatientChartLink,
      iconNode: emrPatientChartLink
        ? phosphorIcon(FolderSimplePlus, '#52c41a')
        : phosphorIcon(ClipboardText, '#52c41a', { weight: 'fill' }),
    });
  }

  if (sftpFallbackOutcome === 'delivered' && autoUploadInProgress && !autoUploadScheduled) {
    return statusResult({
      statusKey: 'auto_upload_in_progress',
      severity: INFO,
      message: 'The record is currently syncing into the patient\'s chart. Refresh the page in a few minutes to see updated status',
      link: emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  if (sftpFallbackOutcome === 'delivered') {
    const message = sftpFallbackReason === 'missing_date_of_service'
      ? 'This document was successfully uploaded to SFTP. The latest EMR upload failed because the date of service was not provided in the document.'
      : 'This document was successfully uploaded to the fallback SFTP folder.';
    return statusResult({
      statusKey: 'sftp_fallback_delivered',
      severity: WARNING,
      message,
      iconNode: phosphorIcon(CloudArrowUp, 'orange'),
    });
  }

  // WARNING, not ERROR, for the same reason as the combination branch above: the chart
  // and the task were both created and only the insurance write failed, so nothing here
  // asks the user to re-file the document. Copy and statusKey are unchanged.
  if (insuranceFailedAndTaskCreated) {
    return statusResult({
      statusKey: 'insurance_failed_and_task_created',
      severity: WARNING,
      message: insuranceFailedAndTaskCreated,
      link: emrPatientChartLink,
      iconNode: antIcon(WarningOutlined, 'orange'),
    });
  }

  if (taskCreationFailedAndInsuranceError) {
    return statusResult({
      statusKey: 'task_creation_failed_and_insurance_error',
      severity: ERROR,
      message: taskCreationFailedAndInsuranceError,
      link: emrPatientChartLink,
      iconNode: antIcon(ExclamationCircleOutlined, '#b31c0e'),
    });
  }

  if (taskCreationError) {
    return statusResult({
      statusKey: 'task_creation_error',
      severity: ERROR,
      message: taskCreationError,
      link: emrPatientChartLink,
      iconNode: taskCreationError === RESTRICTED_CHART_MESSAGE
        ? antIcon(StopOutlined, '#b31c0e')
        : phosphorIcon(ListChecks, '#b31c0e'),
    });
  }

  // The insurance outcome remains visible when writes are disabled; the retry
  // id is present only while the retry action is available.
  if (athenaInsuranceSyncOutcome) {
    const terminalOutcome = ['conflict', 'athena_validation_failed'].includes(athenaInsuranceSyncOutcome);
    const authenticationFailed = athenaInsuranceSyncOutcome === 'authentication_failed';
    const syncDisabled = ['skipped_feature_off', 'skipped_disabled'].includes(athenaInsuranceSyncOutcome);
    let message = 'Chart and document were created; insurance was not added.';
    if (terminalOutcome) {
      message = `${message} Correct the coverage in Athena.`;
    } else if (authenticationFailed) {
      message = `${message} Ask an administrator to verify the Athena integration.`;
    } else if (syncDisabled) {
      message = `${message} Insurance sync is currently disabled.`;
    }
    return statusResult({
      statusKey: 'athena_insurance_sync_error',
      severity: WARNING,
      message,
      retryInsuranceId: athenaInsuranceRetryId,
      iconNode: antIcon(WarningOutlined, '#d48806'),
    });
  }

  // Ahead of chart_creation_error on purpose: the serializer sends both keys for a
  // synchronous chart whose document EMA never confirmed. The chart exists and EMA may
  // already hold the document, so this is an orange "check EMA first" state with the
  // chart link, not the red failure that invites a re-send. Mirrors the identical
  // branch in constants.js -- keep the two in step.
  if (chartFilingUnconfirmed) {
    return statusResult({
      statusKey: 'chart_filing_unconfirmed',
      severity: WARNING,
      message: chartFilingUnconfirmed,
      link: chartFilingUnconfirmedLink,
      iconNode: phosphorIcon(FileMagnifyingGlass, '#d48806'),
    });
  }

  if (chartCreationError) {
    return statusResult({
      statusKey: 'chart_creation_error',
      severity: ERROR,
      message: chartCreationError,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(FolderSimplePlus, '#b31c0e'),
    });
  }

  if (uploadTaskError) {
    return statusResult({
      statusKey: 'upload_task_error',
      severity: ERROR,
      message: uploadTaskError,
      link: emrPatientChartLink,
      iconNode: uploadTaskError === RESTRICTED_CHART_MESSAGE
        ? antIcon(StopOutlined, '#b31c0e')
        : antIcon(ExclamationCircleOutlined, '#b31c0e'),
    });
  }

  if (insuranceError) {
    if (insuranceNotActive) {
      return statusResult({
        statusKey: 'insurance_not_active',
        severity: WARNING,
        message: insuranceNotActive,
        link: emrPatientChartLinkInsurance,
        iconNode: antIcon(WarningOutlined, 'orange'),
      });
    }
    if (primaryInsuranceAlreadyExists) {
      return statusResult({
        statusKey: 'primary_insurance_already_exists',
        severity: WARNING,
        message: primaryInsuranceAlreadyExists,
        link: emrPatientChartLinkInsurance,
        iconNode: antIcon(WarningOutlined, 'orange'),
      });
    }
    return statusResult({
      statusKey: 'insurance_error',
      severity: ERROR,
      message: insuranceError,
      link: emrPatientChartLinkInsurance,
      iconNode: antIcon(ExclamationCircleOutlined, '#b31c0e'),
    });
  }

  if (insurancePopulatedAndTaskCreationError) {
    return statusResult({
      statusKey: 'insurance_populated_and_task_creation_error',
      severity: ERROR,
      message: insurancePopulatedAndTaskCreationError,
      link: emrPatientChartLink,
      iconNode: antIcon(ExclamationCircleOutlined, '#b31c0e'),
    });
  }

  if (chartTaskError && !taskInsuranceInProgress && !insuranceFailedAndTaskCreated) {
    return statusResult({
      statusKey: 'chart_task_error',
      severity: WARNING,
      message: chartTaskError,
      link: emrPatientChartLink,
      iconNode: phosphorIcon(ClipboardPlus, 'orange'),
    });
  }

  if (!importedIntoEMR && emrImportError) {
    if (emrImportError === 'AI detected multiple patients in this record.') {
      return statusResult({
        statusKey: 'emr_import_error_multiple_patients',
        severity: ERROR,
        message: emrImportError,
        link: emrPatientChartLink,
        iconNode: antIcon(TeamOutlined, '#b31c0e'),
      });
    }
    if (emrImportError === FAILED_FIND_PATIENT_CHART) {
      return statusResult({
        statusKey: 'emr_import_error_failed_find_chart',
        severity: ERROR,
        message: findPatientChartMessage(orgName),
        link: emrPatientChartLink,
        iconNode: phosphorIcon(ClipboardText, '#b31c0e', { weight: 'fill' }),
      });
    }
    if (emrImportError === RESTRICTED_CHART_MESSAGE) {
      return statusResult({
        statusKey: 'emr_import_error_restricted_chart',
        severity: WARNING,
        message: emrImportError,
        link: emrPatientChartLink,
        iconNode: antIcon(StopOutlined, 'orange'),
      });
    }
    return statusResult({
      statusKey: 'emr_import_error',
      severity: ERROR,
      message: emrImportError,
      link: emrPatientChartLink,
      iconNode: antIcon(ExclamationCircleOutlined, '#b31c0e'),
    });
  }

  if (chartInProgress) {
    return statusResult({
      statusKey: 'chart_in_progress',
      severity: INFO,
      message: 'Patient Chart is currently being created. Refresh the page in a few minutes to see updated status',
      link: emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  if (autoUploadScheduled) {
    return statusResult({
      statusKey: 'auto_upload_scheduled',
      severity: INFO,
      message: 'Queued for after-hours upload to the EMR. This will be sent automatically tonight.',
      iconNode: antIcon(SyncOutlined, '#1890ff'),
    });
  }

  if (autoUploadInProgress) {
    return statusResult({
      statusKey: 'auto_upload_in_progress',
      severity: INFO,
      message: 'The record is currently syncing into the patient\'s chart. Refresh the page in a few minutes to see updated status',
      link: emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  if (taskInProgress) {
    return statusResult({
      statusKey: 'task_in_progress',
      severity: INFO,
      message: 'A task is currently being created in this patient\'s chart. Refresh the page in a few minutes to see updated status',
      link: emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  if (taskAutoUploadInProgress) {
    return statusResult({
      statusKey: 'task_autoupload_in_progress',
      severity: INFO,
      message: 'The record is currently syncing into the patient\'s chart and a task is being created. Refresh the page in a few minutes to see updated status',
      link: emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  if (taskInsuranceInProgress) {
    return statusResult({
      statusKey: 'task_insurance_in_progress',
      severity: INFO,
      message: 'Patient chart created successfully. Task creation and insurance population are in progress. Refresh the page in a few minutes to see updated status',
      link: emrPatientChartLinkInsurance || emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  if (taskAfterChartInProgress) {
    return statusResult({
      statusKey: 'task_after_chart_creation_in_progress',
      severity: INFO,
      message: 'Patient Chart created successfully and a task is being created. Refresh the page in a few minutes to see updated status',
      link: emrPatientChartLink,
      iconNode: antIcon(SyncOutlined, 'orange', { spin: true }),
    });
  }

  return null;
};

export const getChartStatusForTransmission = (transmission = {}, options = {}) => (
  getPacketUploadStatus(transmission?.packet_upload, transmission, (fields) => {
    const status = getDocumentChartStatus(fields, options);
    return { ...status, label: CHART_STATUS_LABELS[status.statusKey] };
  }) || getDocumentChartStatus(transmission, options)
);

export const canShowChartStatus = ({ currentPage }) => currentPage !== PAGES.SENT;
